import { observer } from 'mobx-react';
import React from 'react';
import {
	Checkbox,
	CommandBar,
	ContextualMenuItemType,
	DefaultButton,
	Dialog,
	DialogFooter,
	DialogType,
	ICommandBarItemProps,
	Label,
	Pivot,
	PivotItem,
	PrimaryButton,
	Spinner,
	Stack,
	StackItem,
	Text,
	TextField,
} from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import {
	IEntityAPI,
	IEntityDashboardConfigurationTemplate,
	IGenericEntity,
	IViewInfo,
} from '../../API';

interface IViewsCtrlProps {
	state: ViewsState;
	renderFormat?: ViewsCtrlRenderFormat;
}

export enum ViewsCtrlRenderFormat {
	CommandMenu,
	Buttons,
	Pivot
}

class ViewsCtrl extends React.Component<IViewsCtrlProps, {}> {
	componentDidMount(): void {
		this.props.state.ensureInitialLoaded();
	}

	render() {
		if (this.props.state.disabled) return <></>;

		switch(this.props.renderFormat) {
			case ViewsCtrlRenderFormat.Buttons:
				return this.getButtons();
			case ViewsCtrlRenderFormat.Pivot:
				return this.getPivot();
			case ViewsCtrlRenderFormat.CommandMenu:
				return this.getCommandMenu();
		}
	}

	getPivot() {
		return <Pivot selectedKey={this.props.state.selected?.id}
					onLinkClick={(p) => {
						
						this.props.state.loadById(p?.props.itemKey || '');
					}}>
				{this.props.state.views.map((t) => {
					return (
						<PivotItem itemKey={t.id} headerText={t.title} />
					);
				})}
		</Pivot>;
	}

	getButtons() {
		return (
			<Stack
				horizontal
				className="pivotSmallButtons"
				key={'pivot-' + this.props.state.selected?.id}
			>
				{this.props.state.views.map((t) => {
					return (
						<StackItem
							className={t.id === this.props.state.selected?.id ? 'selected' : ''}
							key={t.id}
							onClick={() => {
								let key = t.id || '';
								if (key && key === this.props.state.selected?.id) {
									//They clicked on the current item, so unselect it
									key = '';
								}

								this.props.state.loadById(key);
							}}
						>
							{t.title}
						</StackItem>
					);
				})}
			</Stack>
		);
	}

	getCommandMenu() {
		let viewsMenu: ICommandBarItemProps[] = [
			{
				key: 'saveTemplate',
				text: 'Save',
				iconProps: { iconName: 'Save' },
				onClick: () => {
					this.props.state.toggleShowSaveViewDialog();
				},
			},
		];

		if (this.props.state.selected) {

			viewsMenu.push({
				key: 'deleteTemplate',
				text: 'Delete',
				iconProps: { iconName: 'Delete' },
				onClick: () => {
					this.props.state.deleteSelected();
				},
			});
		}

		if (this.props.state.views.length > 0) {
			viewsMenu.push({
				key: 'divider_1',
				itemType: ContextualMenuItemType.Divider,
			});

			this.props.state.views.forEach((t) => {
				viewsMenu.push({
					key: 'view-' + t.id,
					text: t.title,
					canCheck: true,
					checked: t.id === this.props.state.selected?.id,
					onClick: () => {
						if (t.id !== this.props.state.selected?.id) {
							this.props.state.load(t);
						}
					},
				});
			});
		}

		let viewsItem = {
			key: 'views',
			text: this.props.state.viewsButtonLabel,
			subMenuProps: {
				items: viewsMenu,
			},
		};

		return (
			<>
				{this.getSaveDialog()}
				{this.getConfirmDeleteDialog()}
				<CommandBar items={[viewsItem]} />
			</>
		);
	}

	private getConfirmDeleteDialog(): React.ReactElement {
		const modelProps = {
			isBlocking: true,
			styles: { main: { width: '600px' } },
		};

		const dialogContentProps = {
			type: DialogType.normal,
			title: 'Confirm Delete',
		};

		return (
			<Dialog
				dialogContentProps={dialogContentProps}
				modalProps={modelProps}
				hidden={!this.props.state.confirmDeletionVisible}
				onDismiss={() => {
					this.props.state.toggleConfirmDeletionVisible();
				}}
			>
				<Stack>
					<Text>
						Are you sure you wish to delete "{this.props.state.selected?.title}"
					</Text>
				</Stack>

				<DialogFooter>
					<DefaultButton
						style={{ width: 100 }}
						onClick={() => {
							this.props.state.toggleConfirmDeletionVisible();
						}}
					>
						Close
					</DefaultButton>
					<PrimaryButton
						style={{ width: 100 }}
						onClick={() => {
							this.props.state.confirmDeleteSelected();
						}}
					>
						{this.props.state.deletingView && <Spinner></Spinner>}
						Delete
					</PrimaryButton>
				</DialogFooter>
			</Dialog>
		);
	}

	private getSaveDialog(): React.ReactElement {
		const modelProps = {
			isBlocking: true,
			styles: { main: { width: '600px' } },
		};

		const dialogContentProps = {
			type: DialogType.normal,
			title: 'Save',
		};

		return (
			<Dialog
				dialogContentProps={dialogContentProps}
				modalProps={modelProps}
				hidden={!this.props.state.saveViewDialogVisible}
				onDismiss={() => {
					this.props.state.toggleShowSaveViewDialog();
				}}
			>
				<Stack>
					<Label>Name</Label>
					<TextField
						value={this.props.state.saveViewName}
						onChange={(_, t) => {
							this.props.state.setSaveViewName(t || '');
						}}
					/>

					{this.props.state.selected ? (
						<>
							<Label>Replace existing</Label>
							<Checkbox
								checked={this.props.state.saveViewReplace}
								onChange={(_, c) => {
									this.props.state.setSaveViewReplace(!!c);
								}}
								label={
									'Select to replace the existing "' +
									this.props.state.selected?.title +
									'" view.'
								}
							></Checkbox>
						</>
					) : (
						<></>
					)}
				</Stack>

				<DialogFooter>
					<DefaultButton
						style={{ width: 100 }}
						onClick={() => {
							this.props.state.toggleShowSaveViewDialog();
						}}
					>
						Close
					</DefaultButton>
					<PrimaryButton
						disabled={!this.props.state.saveViewName || this.props.state.savingView}
						style={{ width: 100 }}
						onClick={() => {
							this.props.state.save();
						}}
					>
						{this.props.state.savingView && <Spinner></Spinner>}
						Save
					</PrimaryButton>
				</DialogFooter>
			</Dialog>
		);
	}
}

export class ViewsState {
	private getApi: () => Promise<IEntityAPI<IGenericEntity>>;

	public views: IViewInfo[] = [];
	public selected: IViewInfo | null = null;
	private loaded: boolean = false;
	public saveViewName: string = '';
	public savingView: boolean = false;
	public saveViewDialogVisible: boolean = false;
	public saveViewReplace: boolean = false;

	public viewsButtonLabel: string;

	private onViewSelect: (t: IViewInfo) => void;
	private getCurrentViewConfig: () => any;
	private getCurrentViewJson: () => string;

	public disabled: boolean = false;

	constructor(
		buttonLabel: string,
		getApi: () => Promise<IEntityAPI<IGenericEntity>>,
		onViewSelect: (t: IViewInfo) => void,
		getCurrentViewJson: () => string,
		getCurrentViewConfig: () => IEntityDashboardConfigurationTemplate
	) {
		makeAutoObservable(this);
		this.viewsButtonLabel = buttonLabel;
		this.getApi = getApi;
		this.onViewSelect = onViewSelect;
		this.getCurrentViewConfig = getCurrentViewConfig;
		this.getCurrentViewJson = getCurrentViewJson;
	}

	public disable() {
		this.disabled = true;
	}

	public async ensureInitialLoaded(): Promise<void> {
		if (!this.loaded && !this.disabled) {
			this.loaded = true;
			let result = await (await this.getApi()).views();
			this.setViews(result);
			if(result.length) {
				this.load(result[0]);
			}
		}
	}

	public load(t: IViewInfo) {
		this.selected = t;
		this.onViewSelect(t);
	}

	loadById(arg0: string) {
		let t = this.views.find((t) => t.id === arg0);
		if (t) {
			this.load(t);
		}
	}

	public setSaveViewReplace(val: boolean) {
		this.saveViewReplace = val;
	}

	public confirmDeletionVisible: boolean = false;
	public deletingView: boolean = false;
	public toggleConfirmDeletionVisible() {
		this.confirmDeletionVisible = !this.confirmDeletionVisible;
	}

	public deleteSelected() {
		this.confirmDeletionVisible = true;
	}

	public clearSelected() {
		this.selected = null;
	}

	public async confirmDeleteSelected() {
		this.confirmDeletionVisible = false;
		if (this.selected) {
			this.deletingView = true;
			let id = this.selected.id;
			this.selected = null;
			this.views = this.views.filter((v) => v.id !== id);

			//Do the delete
			await (await this.getApi()).deleteView(id);

			this.deletingView = false;
		}
	}

	setSaveViewName(arg0: string) {
		this.saveViewName = arg0;
	}

	toggleShowSaveViewDialog() {
		this.saveViewDialogVisible = !this.saveViewDialogVisible;

		if (this.saveViewDialogVisible) {
			if (this.selected) {
				this.saveViewReplace = true;
				this.saveViewName = this.selected.title;
			} else {
				this.saveViewName = '';
			}
		}
	}

	public async save(): Promise<void> {
		this.savingView = true;
		let view: IViewInfo = {
			id: this.saveViewReplace ? this.selected?.id || '' : '',
			title: this.saveViewName,
			viewJson: JSON.stringify(this.getCurrentViewJson()),
			config: this.getCurrentViewConfig(),
		};

		view.id = await (await this.getApi()).updateView(view);

		if (this.saveViewReplace) {
			//Update the existing view
			let existing = this.views.find((t) => t.id === this.selected?.id);
			if (existing) {
				existing.title = view.title;
				existing.viewJson = view.viewJson;
			}
		} else {
			this.views.push(view);
			this.selected = view;
		}

		this.savingView = false;
		this.toggleShowSaveViewDialog();
	}

	setViews(views: IViewInfo[]) {
		this.views = views;
	}
}

export default observer(ViewsCtrl);
